import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import PropertyOffersAPI from 'dashboard/api/propertyOffers';
import SalesTransactionsAPI from 'dashboard/api/salesTransactions';

const errorMessage = (error, fallback) =>
  error?.response?.data?.errors?.[0] ||
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  fallback;

export function useSalesDeliverySignature({ id, t, notify }) {
  const transaction = ref(null);
  const loading = ref(false);
  const loadError = ref(false);
  const resendingSignerReference = ref('');

  const integration = computed(
    () => transaction.value?.receipt?.integration || null
  );
  const propertyOfferId = computed(() => transaction.value?.property_offer_id);
  const canManage = computed(() =>
    Boolean(
      propertyOfferId.value && transaction.value?.permissions?.can_save_draft
    )
  );

  const load = async () => {
    if (!id.value) return null;
    loading.value = true;
    loadError.value = false;
    try {
      const response = await SalesTransactionsAPI.show(id.value);
      transaction.value = response?.data || null;
      return transaction.value;
    } catch (error) {
      loadError.value = true;
      notify(errorMessage(error, t('SALES.DELIVERY.CHECKOUT.ERRORS.LOAD')));
      return null;
    } finally {
      loading.value = false;
    }
  };

  const resendSigner = async signer => {
    const reference = signer?.signer_reference;
    if (!canManage.value || !reference) return null;

    resendingSignerReference.value = reference;
    try {
      await PropertyOffersAPI.resendContractSigner(
        propertyOfferId.value,
        reference
      );
      notify(
        t('SALES.DETAIL.RECEIPT.RESEND_SUCCESS', {
          name: signer.display_name,
        })
      );
      return load();
    } catch (error) {
      notify(errorMessage(error, t('SALES.DETAIL.RECEIPT.ERROR_RESEND')));
      return null;
    } finally {
      resendingSignerReference.value = '';
    }
  };

  if (getCurrentInstance()) onMounted(load);

  return {
    canManage,
    integration,
    load,
    loadError,
    loading,
    propertyOfferId,
    resendSigner,
    resendingSignerReference,
    transaction,
  };
}
